// kHtmlCloseIndexCore — where an element's closing tag is, for kHtmlParserCore.BuildTree.
//
// ONE concern: for ONE text, answer "at which index is the closing tag that matches an opening tag of
// this name, searching from here?" - counting nested tags of the same name, -1 where there is none.
//
// WHY IT EXISTS (DocumentBuilder PX-128, 2026-10-01). The parser asked this once per element with a loop
// that lower-cased the WHOLE text first and searched on to the END of it when no further tag of that name
// existed. That is quadratic in the number of blocks: 125 blocks cost 200-440 ms of script when a text
// part was drawn again, 1000 blocks 2.3 s. Here the text is lower-cased ONCE, and each tag name's open /
// close positions are read ONCE and paired with a stack; an answer is then a lookup.
//
// THE ANSWERS ARE THE ORIGINAL LOOP'S, FOR EVERY INPUT - malformed, unclosed and crossed markup included.
// The original loop is kept (_ScanFrom) and is still what runs where lower-casing changes the text's
// length (U+0130 becomes two code units), because the original then compared positions of the LOWER-CASED
// text with the length of the text as written, and only the loop itself reproduces that.
// Proof: ReUsableTS\tests\kHtmlParserCore.closeIndex.test.js.
//
// A second consumer needs nothing changed: the class knows no parser type, only a text and a tag name.

import { getCoreUtils } from "./kCoreHandler";

/** One tag name's open and close tags in a text, in text order, each open paired with its close. */
export interface IkTagEvents {
    /** Index in the text of each `<name` (only where the name really ends there) and each `</name>`. */
    Pos: number[];
    /** True for a closing tag. */
    IsClose: boolean[];
    /** For an opening tag: the event number of the closing tag that matches it, -1 where none does. */
    Match: number[];
}

export class kHtmlCloseIndexCore {

    /**
     * Characters read so far, by every instance: one per character lower-cased, searched or paired.
     * It is how the tests prove the lookup is linear without a clock; nothing else reads it.
     */
    public static CharsVisited: number = 0;

    private Html: string = "";
    private Lower: string | null = null;
    private Tables: Map<string, IkTagEvents> = new Map<string, IkTagEvents>();

    /** The index for one text. Nothing is read until the first Find. */
    public static For = (Html: string): kHtmlCloseIndexCore => {
        if (getCoreUtils().KErrorMode) { return new kHtmlCloseIndexCore(); }
        try {
            const Index: kHtmlCloseIndexCore = new kHtmlCloseIndexCore();
            Index.Html = Html;
            return Index;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlCloseIndexCore.For");
            return new kHtmlCloseIndexCore();
        }
    };

    /**
     * Index of the closing tag that MATCHES an opening tag of this name, searching from From and
     * counting nested same-name tags. -1 where there is none.
     */
    public Find = (TagName: string, From: number): number => {
        if (getCoreUtils().KErrorMode) { return -1; }
        try {
            const Lower: string = this._LowerText();
            const LowerName: string = TagName.toLowerCase();
            // Positions of the lower-cased text only mean the same in the text as written while both have one length.
            if (Lower.length !== this.Html.length || LowerName.length !== TagName.length) {
                return this._ScanFrom(TagName, From);
            }
            const Table: IkTagEvents = this._TableFor(LowerName);
            let At: number = kHtmlCloseIndexCore._FirstAtOrAfter(Table.Pos, From);
            while (At < Table.Pos.length) {
                // A closing tag here closes OUR element: every open since From has been stepped over with its close.
                if (Table.IsClose[At] === true) { return Table.Pos[At]; }
                // A nested open that is never closed takes every later close for itself.
                if (Table.Match[At] === -1) { return -1; }
                At = Table.Match[At] + 1;
            }
            return -1;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlCloseIndexCore.Find");
            return -1;
        }
    };

    /** True where the character after a tag name really ends it — so "<p" does not match "<pre". */
    public static EndsTagName = (After: string): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            if (After === " " || After === ">" || After === "/") { return true; }
            return After === "\t" || After === "\n";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlCloseIndexCore.EndsTagName");
            return false;
        }
    };

    /** The text lower-cased, made once. */
    private _LowerText = (): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            if (this.Lower === null) {
                this.Lower = this.Html.toLowerCase();
                kHtmlCloseIndexCore.CharsVisited = kHtmlCloseIndexCore.CharsVisited + this.Html.length;
            }
            return this.Lower;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlCloseIndexCore._LowerText");
            return "";
        }
    };

    /** One tag name's events, read from the text the first time the name is asked for. */
    private _TableFor = (LowerName: string): IkTagEvents => {
        if (getCoreUtils().KErrorMode) { return { Pos: [], IsClose: [], Match: [] }; }
        try {
            const Known: IkTagEvents | undefined = this.Tables.get(LowerName);
            if (Known !== undefined) { return Known; }
            const Opens: number[] = this._Positions("<" + LowerName, LowerName.length);
            const Closes: number[] = this._Positions("</" + LowerName + ">", -1);
            const Table: IkTagEvents = kHtmlCloseIndexCore._Pair(Opens, Closes);
            this.Tables.set(LowerName, Table);
            return Table;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlCloseIndexCore._TableFor");
            return { Pos: [], IsClose: [], Match: [] };
        }
    };

    /**
     * Every index at which Pattern stands in the lower-cased text, overlapping ones included.
     * NameLength >= 0 marks an OPENING tag: it is kept only where the name really ends after it,
     * read from the text as written - the original loop's test. -1 keeps every occurrence.
     */
    private _Positions = (Pattern: string, NameLength: number): number[] => {
        if (getCoreUtils().KErrorMode) { return []; }
        try {
            const Lower: string = this._LowerText();
            const Found: number[] = [];
            let At: number = Lower.indexOf(Pattern, 0);
            while (At !== -1) {
                if (NameLength < 0 || kHtmlCloseIndexCore.EndsTagName(this.Html.charAt(At + 1 + NameLength)) === true) {
                    Found.push(At);
                }
                At = Lower.indexOf(Pattern, At + 1);
            }
            kHtmlCloseIndexCore.CharsVisited = kHtmlCloseIndexCore.CharsVisited + Lower.length;
            return Found;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlCloseIndexCore._Positions");
            return [];
        }
    };

    /** Opens and closes merged in text order, each close paired with the nearest open still waiting. */
    private static _Pair = (Opens: number[], Closes: number[]): IkTagEvents => {
        if (getCoreUtils().KErrorMode) { return { Pos: [], IsClose: [], Match: [] }; }
        try {
            const Table: IkTagEvents = { Pos: [], IsClose: [], Match: [] };
            const Waiting: number[] = [];
            let O: number = 0;
            let C: number = 0;
            while (O < Opens.length || C < Closes.length) {
                const TakeOpen: boolean = O < Opens.length && (C >= Closes.length || Opens[O] < Closes[C]);
                Table.IsClose.push(TakeOpen === false);
                Table.Match.push(-1);
                if (TakeOpen === true) {
                    Waiting.push(Table.Pos.length);
                    Table.Pos.push(Opens[O]);
                    O = O + 1;
                    continue;
                }
                if (Waiting.length > 0) { Table.Match[Waiting.pop() as number] = Table.Pos.length; }
                Table.Pos.push(Closes[C]);
                C = C + 1;
            }
            kHtmlCloseIndexCore.CharsVisited = kHtmlCloseIndexCore.CharsVisited + Table.Pos.length;
            return Table;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlCloseIndexCore._Pair");
            return { Pos: [], IsClose: [], Match: [] };
        }
    };

    /** Number of the first event at or after From (binary search); Pos.length where there is none. */
    private static _FirstAtOrAfter = (Pos: number[], From: number): number => {
        if (getCoreUtils().KErrorMode) { return 0; }
        try {
            let Low: number = 0;
            let High: number = Pos.length;
            while (Low < High) {
                const Mid: number = (Low + High) >> 1;
                if (Pos[Mid] < From) {
                    Low = Mid + 1;
                } else {
                    High = Mid;
                }
            }
            kHtmlCloseIndexCore.CharsVisited = kHtmlCloseIndexCore.CharsVisited + 1;
            return Low;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlCloseIndexCore._FirstAtOrAfter");
            return 0;
        }
    };

    /**
     * The ORIGINAL loop (kHtmlParserCore._FindCloseIndex before PX-128), unchanged but for reading the
     * lower-cased text made once. Used only where lower-casing changes the text's length.
     */
    private _ScanFrom = (TagName: string, From: number): number => {
        if (getCoreUtils().KErrorMode) { return -1; }
        try {
            const Lower: string = this._LowerText();
            const OpenTag: string = "<" + TagName.toLowerCase();
            const CloseTag: string = "</" + TagName.toLowerCase() + ">";
            let Depth: number = 1;
            let SearchIdx: number = From;
            while (SearchIdx < this.Html.length) {
                const NextOpen: number = Lower.indexOf(OpenTag, SearchIdx);
                const NextClose: number = Lower.indexOf(CloseTag, SearchIdx);
                if (NextClose === -1) { return -1; }
                if (NextOpen !== -1 && NextOpen < NextClose) {
                    const After: string = this.Html.charAt(NextOpen + 1 + TagName.length);
                    if (kHtmlCloseIndexCore.EndsTagName(After) === true) { Depth = Depth + 1; }
                    SearchIdx = NextOpen + 1;
                    continue;
                }
                Depth = Depth - 1;
                if (Depth === 0) { return NextClose; }
                SearchIdx = NextClose + 1;
            }
            return -1;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlCloseIndexCore._ScanFrom");
            return -1;
        }
    };
}
